'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { LoaderCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useChangelogStore, type InboxChangelog } from '@/stores/changelogStore'
import { useLocale, useTranslations } from 'next-intl'
import { popModal } from '@/components/modals'
import { cn } from '@/lib/utils'
import { getLocalizedPath, formatRelativeDate } from '@/lib/i18n-utils'
import type { Locale } from '@/i18n/config'
import directusImageLoader from '@/lib/directus-image-loader'
import { DraftBadge } from '@/components/blog/draft-badge'
import { IS_PROD } from '@/lib/constant'
import { findChangelogTranslation } from '@/lib/changelog-translations'

function safeSlug(slug?: string | null) {
  return (slug || '').trim()
}
const ChangelogItem = ({
  changelog,
  onItemClick,
  variant = 'default',
}: {
  changelog: InboxChangelog
  onItemClick: (id: string) => void
  variant?: 'featured' | 'default'
}) => {
  const locale = useLocale()
  const imageId =
    changelog.cover_image && typeof changelog.cover_image === 'object'
      ? changelog.cover_image.id
      : changelog.cover_image
  const imageUrl = imageId || '/og-image/placeholder.png'
  const path = getLocalizedPath(
    locale as Locale,
    `/changelog/${safeSlug(changelog.slug)}`,
  )
  const isFeatured = variant === 'featured'
  const relativeDate = changelog.pub_date
    ? formatRelativeDate(changelog.pub_date, locale)
    : ''

  const translation = findChangelogTranslation(changelog, locale as Locale, {
    fallbackToAny: !IS_PROD,
  })

  if (!translation && !changelog.title) return null

  const title = translation?.title || changelog.title
  const headline = translation?.headline || ''

  return (
    <div className={cn(!isFeatured && 'border-t border-white/10')}>
      <Link
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'block p-4 hover:bg-white/5 rounded-lg transition-colors group',
          isFeatured && 'pt-2',
        )}
        onClick={() => onItemClick(changelog.id)}
      >
        {isFeatured ? (
          <div>
            <div className="flex items-center gap-2">
              {!changelog.isRead && (
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
              )}
              <h4 className="font-semibold text-white group-hover:text-blue-400 transition-colors break-words">
                {title}
              </h4>
              <DraftBadge draft={changelog.status !== 'published'} />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">{relativeDate}</p>
            <p className="text-sm text-gray-400 line-clamp-2 mt-2 break-words">
              {headline}
            </p>
            <div className="relative w-full h-40 rounded-md overflow-hidden mt-4">
              <Image
                loader={directusImageLoader}
                src={imageUrl}
                alt={title || 'Changelog feature image'}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                {!changelog.isRead && (
                  <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                )}
                <h4 className="font-semibold text-white group-hover:text-blue-400 transition-colors truncate">
                  {title}
                </h4>
                <DraftBadge draft={changelog.status !== 'published'} />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">{relativeDate}</p>
              {headline && (
                <p className="text-sm text-gray-400 line-clamp-2 mt-1 break-words">
                  {headline}
                </p>
              )}
            </div>
            <div className="relative w-20 h-16 flex-shrink-0 rounded-md overflow-hidden">
              <Image
                loader={directusImageLoader}
                src={imageUrl}
                alt={title || 'Changelog feature image'}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        )}
      </Link>
    </div>
  )
}

export const ChangelogModal = ({ name }: { name: 'ChangelogModal' }) => {
  const { changelogs, loading, error, markAsRead } = useChangelogStore()
  const locale = useLocale()
  const t = useTranslations('changelog_inbox')

  const filteredChangelogs = React.useMemo(() => {
    return changelogs.filter((cl) => {
      const statusOk = IS_PROD
        ? cl.status === 'published'
        : cl.status === 'published' || cl.status === 'draft'
      const translation = findChangelogTranslation(cl, locale as Locale, {
        fallbackToAny: !IS_PROD,
      })
      return statusOk && !!translation
    })
  }, [changelogs, locale])

  return (
    <Dialog open={true} onOpenChange={() => popModal(name)}>
      <DialogContent
        className="
        sm:w-[520px] sm:h-[668px]
        w-full h-[80vh] sm:bottom-auto
        bg-gradient-to-[104deg] from-[#222222] via-[#2B2B2B] to-[#010101]
        border-white/10
        rounded-t-xl sm:rounded-xl
        p-0 flex flex-col gap-4
      "
      >
        <DialogHeader className="p-5 border-b border-white/10 flex-shrink-0">
          <DialogTitle className="font-bold text-lg">{t('title')}</DialogTitle>
        </DialogHeader>
        <div className="flex-grow overflow-y-auto px-3 scrollbar-thin scrollbar-thumb-gray-700 hover:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <LoaderCircle className="animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500 h-full flex items-center justify-center">
              <p>{t('error_loading')}</p>
            </div>
          ) : filteredChangelogs.length > 0 ? (
            <>
              {filteredChangelogs.map((changelog, index) => (
                <ChangelogItem
                  key={changelog.id}
                  changelog={changelog}
                  onItemClick={markAsRead}
                  variant={index === 0 ? 'featured' : 'default'}
                />
              ))}
              <div className="p-4 mt-2 text-center">
                <Link
                  href={getLocalizedPath(locale as Locale, '/changelog')}
                  className="text-sm text-blue-400 hover:underline"
                  onClick={() => popModal(name)}
                >
                  {t('find_more')}
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-10 text-gray-400 h-full flex items-center justify-center">
              <p>{t('no_updates')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
